import { useRef, useState } from 'react';
// material
import { Box, Typography, Divider, Link, Stack, Badge, Avatar } from '@mui/material';
import PropTypes from 'prop-types';

import moment from 'moment';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import MenuPopover from '../MenuPopover';
import { speedConverter } from '../../common/utils/unitConverter';
import { useTranslation } from '../../common/LocalizationProvider';
import FlightReviewSpeedDial from './FlightReviewSpeedDial';
import Scrollbar from '../Scrollbar';
import { selectSession } from '../../createSelectorUtils';

// ----------------------------------------------------------------------

FlightReviewSideBarHeader.propTypes = {
  reportTrip: PropTypes.object,
  device: PropTypes.object,
  state: PropTypes.object,
  setState: PropTypes.func
};

export default function FlightReviewSideBarHeader({ reportTrip, device, state, setState }) {
  const t = useTranslation();
  const ref = useRef(null);
  const location = useLocation();

  const [isOpen, setIsOpen] = useState(false);

  const session = useSelector(selectSession);

  return (
    <>
      <Stack direction="row">
        <Stack direction="row" sx={{ ml: 3, mr: 3, mt: 3, mb: 2 }} ref={ref}>
          <Typography variant="subtitle1" gutterBottom>
            <Link onClick={() => setIsOpen(true)} key="InfoDevice">
              {reportTrip ? reportTrip.deviceName : ''}
            </Link>
          </Typography>
          <Typography variant="subtitle1" gutterBottom sx={{ ml: 1 }}>
            -
          </Typography>
          <Typography variant="subtitle1" gutterBottom sx={{ ml: 1 }}>
            {reportTrip ? moment(reportTrip.startTime).format('DD/MM/YYYY HH:mm') : ''}
          </Typography>
        </Stack>
        <FlightReviewSpeedDial
          reportTrip={reportTrip}
          privateAccount={location.state.privateAccount}
          state={state}
          setState={setState}
        />
      </Stack>
      <MenuPopover
        open={isOpen}
        onClose={() => setIsOpen(false)}
        anchorEl={ref.current}
        sx={{ width: 400 }}
      >
        <Scrollbar>
          <Box sx={{ my: 1.5, px: 2.5 }}>
            <Typography variant="subtitle1" noWrap>
              {device.name}
            </Typography>
          </Box>

          <Divider sx={{ my: 1 }} />

          <Box sx={{ my: 1.5, px: 2.5 }}>
            <Stack alignItems="center">
              <Badge
                overlap="circular"
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right'
                }}
              >
                <Avatar
                  alt="DeviceImage"
                  sx={{ width: 100, height: 100 }}
                  src={
                    device.image === null
                      ? '/static/illustrations/illustration-plane.svg'
                      : device.image
                  }
                />
              </Badge>
            </Stack>
            <Typography variant="subtitle1" gutterBottom color="secondary">
              {t('DL_Form_ParamRoulage')}
            </Typography>

            <Stack direction="row" spacing={1}>
              <Typography variant="subtitle1" gutterBottom>
                {`- ${t('FR_Details_SpeedMax')}:`}
              </Typography>
              <Typography variant="subtitle1" gutterBottom color="primary">
                {`${speedConverter(device.speedMaxRoulage, session.uniteVitesse) || ''} ${
                  session.uniteVitesse
                }`}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1}>
              <Typography variant="subtitle1" gutterBottom>
                {`- ${t('DB_GAlert')}:`}
              </Typography>
              <Typography variant="subtitle1" gutterBottom color="primary">
                {device.gRoulageMinAlert || ''}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1}>
              <Typography variant="subtitle1" gutterBottom>
                {`- ${t('DB_GWarning')}:`}
              </Typography>

              <Typography variant="subtitle1" gutterBottom color="primary">
                {device.gRoulageMinWarning || ''}
              </Typography>
            </Stack>

            <Divider />
            <Typography variant="subtitle1" gutterBottom color="secondary">
              {t('DL_Form_ParamDecollage')}
            </Typography>

            <Stack direction="row" spacing={1}>
              <Typography variant="subtitle1" gutterBottom>
                {`- ${t('FR_PlaneInfo_SpeedMinTakeOff')}:`}
              </Typography>

              <Typography variant="subtitle1" gutterBottom color="primary">
                {`${speedConverter(device.speedMinMontee, session.uniteVitesse) || ''} ${
                  session.uniteVitesse
                }`}
              </Typography>
            </Stack>

            <Divider />
            <Typography variant="subtitle1" gutterBottom color="secondary">
              {t('DL_Form_ParamFlight')}
            </Typography>

            <Stack direction="row" spacing={1}>
              <Typography variant="subtitle1" gutterBottom>
                {`- ${t('FR_PlaneInfo_VNE')}:`}
              </Typography>
              <Typography variant="subtitle1" gutterBottom color="primary">
                {`${speedConverter(device.vne, session.uniteVitesse) || ''} ${
                  session.uniteVitesse
                }`}
              </Typography>
            </Stack>

            <Stack direction="row" spacing={1}>
              <Typography variant="subtitle1" gutterBottom>
                {`- ${t('DB_GAlert')}:`}
              </Typography>

              <Typography variant="subtitle1" gutterBottom color="primary">
                {device.gVolMinAlert || ''}
              </Typography>
            </Stack>

            <Divider />
            <Typography variant="subtitle1" gutterBottom color="secondary">
              {t('DL_Form_ParamLanding')}
            </Typography>

            <Stack direction="row" spacing={1}>
              <Typography variant="subtitle1" gutterBottom>
                {`- ${t('FR_PlaneInfo_SpeedMinAlert')}:`}
              </Typography>

              <Typography variant="subtitle1" gutterBottom color="primary">
                {`${speedConverter(device.speedMaxAtterrissageAlert, session.uniteVitesse) || ''} ${
                  session.uniteVitesse
                }`}
              </Typography>
            </Stack>

            <Stack direction="row" spacing={1}>
              <Typography variant="subtitle1" gutterBottom>
                {`- ${t('FR_PlaneInfo_SpeedMinWarning')}:`}
              </Typography>
              <Typography variant="subtitle1" gutterBottom color="primary">
                {`${
                  speedConverter(device.speedMaxAtterrissageWarning, session.uniteVitesse) || ''
                } ${session.uniteVitesse}`}
              </Typography>
            </Stack>

            <Stack direction="row" spacing={1}>
              <Typography variant="subtitle1" gutterBottom>
                {`- ${t('DB_GAlert')}:`}
              </Typography>
              <Typography variant="subtitle1" gutterBottom color="primary">
                {device.gAtterrissageMinAlert || ''}
              </Typography>
            </Stack>

            <Stack direction="row" spacing={1}>
              <Typography variant="subtitle1" gutterBottom>
                {`- ${t('DB_GWarning')}:`}
              </Typography>
              <Typography variant="subtitle1" gutterBottom color="primary">
                {device.gAtterrissageMinWarning || ''}
              </Typography>
            </Stack>
          </Box>
        </Scrollbar>
      </MenuPopover>
    </>
  );
}
